import { useCallback, useEffect, useMemo, useState } from 'react'
import { Field, Form } from 'react-final-form'
import { FontAwesomeSvgIcon as FontAwesomeIcon } from 'react-fontawesome-svg-icon'
import { useToasts } from 'react-toast-notifications'

import { faAngleDoubleRight } from '@fortawesome/pro-light-svg-icons/faAngleDoubleRight'
import { faExclamationTriangle } from '@fortawesome/pro-light-svg-icons/faExclamationTriangle'
import { faSpinnerThird } from '@fortawesome/pro-light-svg-icons/faSpinnerThird'
import { faTimes } from '@fortawesome/pro-light-svg-icons/faTimes'
import clsx from 'clsx'
import get from 'lodash-es/get'
import noop from 'lodash-es/noop'

import { Coupon, Offerings, Subscription, TierOfferings } from '@infura/packages.api'
import { buildServicePlanInput } from '@infura/packages.common/servicePlan'
import { getUserCreditAddonQuantity } from '@infura/packages.common/user'
import { ActionLink } from '@infura/packages.ui/ActionLink'
import { Button } from '@infura/packages.ui/Button'
import { Card } from '@infura/packages.ui/Card'
import { Heading } from '@infura/packages.ui/Heading'
import { Input } from '@infura/packages.ui/Input'
import { Loader } from '@infura/packages.ui/Loader'
import { Text } from '@infura/packages.ui/Text'
import { ServiceAddon, ServiceTier } from '@infura/packages.uks'

import { PaymentFormState } from 'components/billing/PaymentFormState'
import { sessionStoragePromoCode } from 'components/checkout/Checkout'
import { useCheckoutContext } from 'components/checkout/Context'

import useCard from 'hooks/useCard'
import useSubscription from 'hooks/useSubscription'
import useSubscriptionPreview from 'hooks/useSubscriptionPreview'

import { api } from 'lib/api/api'
import dayjs from 'lib/dayjs'
import { getIsDowngrading, getTierByProductHandle } from 'lib/utils/billing'
import { isChargifyCouponUsedUp as isCouponUsedUp } from 'lib/utils/chargify'
import { getOnCreditPlan } from 'lib/utils/creditPlan'
import { displayAsMillions, numberWithCommas } from 'lib/utils/formatting'

export const SUBSCRIPTION_SUMMARY_TITLE_DATA_TEST_ID = 'subscription-summary-title'
//TODO: lifting this up from the component, but it should not be hardcoded
const REQUEST_PACK_PRICE = 200

const money = new Intl.NumberFormat('en-US', { currency: 'USD', style: 'currency' })

type SubscriptionSummaryTaxesProps = {
  selectedPlan: ServiceTier
  isPaymentFormVisible: boolean
  coupon: Coupon | undefined
  estimatedTax: number | null
  setEstimatedTax: (estimatedTax: number) => void
  setIsTaxLoading: (isTaxLoading: boolean) => void
  requestPacks?: number
}

const SubscriptionSummaryTaxes = ({
  coupon,
  estimatedTax,
  isPaymentFormVisible,
  requestPacks,
  selectedPlan,
  setEstimatedTax,
  setIsTaxLoading
}: Readonly<SubscriptionSummaryTaxesProps>) => {
  const { loading, preview } = useSubscriptionPreview({
    promoCode: coupon?.code,
    requestPacks,
    tier: selectedPlan
  })
  useEffect(() => {
    if (!isPaymentFormVisible && !loading && preview && preview.data) {
      // @ts-expect-error enabled to support strictNullChecks - Please fix this if you can!
      const { total_tax_in_cents } = preview.data.subscription_preview.next_billing_manifest
      setEstimatedTax(Number(total_tax_in_cents / 100))
    }
    if (!loading) {
      setIsTaxLoading(false)
    }
  }, [preview, loading, isPaymentFormVisible, setEstimatedTax, setIsTaxLoading])

  if (loading) {
    return <Loader className='w-18 h-[21px]' pulse />
  }

  return estimatedTax && estimatedTax > 0 ? `US${money.format(estimatedTax)}` : "Couldn't calculate taxes"
}

type SubscriptionSummaryProps = {
  isPaymentFormVisible: boolean
  onSubscriptionUpdated?: (formValues: PaymentFormState, subscription?: Subscription) => void
  offerings: Offerings
}

export default function SubscriptionSummary({
  isPaymentFormVisible,
  offerings: { addons, tiers },
  onSubscriptionUpdated = noop
}: Readonly<SubscriptionSummaryProps>) {
  const {
    checkoutCloseCallback,
    coupon,
    couponLoading,
    currentRequestPacksQuantity,
    currentThroughputAddonEnabled,
    requestPacksQuantity,
    revalidateUser,
    selectedPlan,
    servicePlan,
    setCoupon,
    throughputAddonEnabled,
    user
  } = useCheckoutContext()

  const isOnCreditPlan = getOnCreditPlan(servicePlan)
  const [isCouponCodeFormVisible, setIsCouponCodeFormVisible] = useState<boolean>(false)
  const [couponCode, setCouponCode] = useState<string>('')
  const [couponCodeError, setCouponCodeError] = useState<string>('')
  const [validatingCoupon, setValidatingCoupon] = useState<boolean>(false)
  const [isPaymentSubmitting, setIsPaymentSubmitting] = useState<boolean>(false)
  const [estimatedTax, setEstimatedTax] = useState<number>(0)
  const [isTaxLoading, setIsTaxLoading] = useState<boolean>(true)
  const { addToast } = useToasts()
  const chargify = useSubscription()
  const { card: chargifyCard } = useCard()
  const hasPaymentMethod = !!chargifyCard
  const endPeriod = chargify.subscription ? chargify?.subscription?.current_period_ends_at : null
  const dueDate = endPeriod ? dayjs(endPeriod).format('MMM D, YYYY') : dayjs().add(1, 'month').format('MMM D, YYYY')
  const formState = {
    coupon_code: ''
  }
  const handleValidatePromoCode = useCallback(async () => {
    try {
      setValidatingCoupon(true)
      const {
        data: { coupon: newCoupon }
      } = (await api.getCoupon(couponCode)).data

      setCoupon(newCoupon)
      setCouponCode(newCoupon?.code)
      setIsCouponCodeFormVisible(false)
      sessionStorage.setItem(sessionStoragePromoCode, JSON.stringify(newCoupon))

      if (isCouponUsedUp(chargify?.subscription.coupons, newCoupon?.code)) {
        throw Error('coupon is used up')
      }
    } catch (e: any) {
      setCouponCodeError('Invalid discount code')
    } finally {
      setValidatingCoupon(false)
    }
  }, [couponCode, chargify?.subscription?.coupons, setCoupon])

  // This function is called when the user submits the payment form
  const handleSubscription = useCallback(
    async ({ tier, ...values }: PaymentFormState) => {
      try {
        setIsPaymentSubmitting(true)
        const addons = values.addons.filter(addon => addon.selected)
        const trafficAddon = addons.find(addon => addon.type === ServiceAddon.TRAFFIC_ADDON)
        const throughputAddonEnabled =
          addons.find(addon => addon.type === ServiceAddon.THROUGHPUT_ADDON)?.selected ?? false

        // Why are we sending a service plan change here?
        // It's because this API endpoint is actually used to update chargify.
        // We should most likely rename it
        const servicePlanInput = buildServicePlanInput({
          newTier: tier,
          reqAddonQty: trafficAddon?.quantity ?? 0,
          throughputAddonEnabled
        })
        const res = await api.updateServicePlan(user?.id ?? '', coupon?.code, servicePlanInput)

        if (tier !== ServiceTier.CORE && (window as any).twttr) {
          ;(window as any).twttr.conversion.trackPid('o4pn1', {
            tw_order_quantity: String(1 + addons.length),
            tw_sale_amount: tiers[tier as string].price
          })
        }

        revalidateUser()

        if (sessionStorage.hasOwnProperty(sessionStoragePromoCode)) {
          sessionStorage.removeItem(sessionStoragePromoCode)
        }

        onSubscriptionUpdated({ tier, ...values }, res?.data?.data)
      } catch (e: any) {
        const {
          data: {
            error: { message }
          }
        } = e.response

        addToast(message, {
          appearance: 'error',
          autoDismiss: true
        })
      } finally {
        setIsPaymentSubmitting(false)
      }
    },
    [user, onSubscriptionUpdated, addToast, revalidateUser, coupon, isOnCreditPlan, tiers]
  )

  const currentPlan = getTierByProductHandle(chargify?.subscription?.product.handle || 'core')
  const isPlanUnchanged = currentPlan === selectedPlan
  const isAddonUnchanged = requestPacksQuantity === getUserCreditAddonQuantity(servicePlan)
  const isDowngradingToFree = selectedPlan === ServiceTier.CORE
  const isPlanDowngrade = getIsDowngrading(currentPlan, selectedPlan)
  const isAddonDowngrade =
    requestPacksQuantity < getUserCreditAddonQuantity(servicePlan) ||
    (!throughputAddonEnabled && currentThroughputAddonEnabled)
  const isDowngrading = isPlanDowngrade || (isPlanUnchanged && isAddonDowngrade)

  const selectedPlanPrice = tiers?.[selectedPlan]?.price ?? 0
  const currentPlanPrice = tiers?.[currentPlan]?.price ?? 0
  const amountOff = useMemo(() => {
    if (coupon) {
      return Math.min(coupon.amount ?? 0, selectedPlanPrice)
    }
    return 0
  }, [coupon, selectedPlanPrice])

  const percentOff = useMemo(() => {
    const percentage = Number(get(coupon, 'percentage', 0)) || Number(amountOff / selectedPlanPrice) * 100 || 0

    return Number(Math.min(percentage, 100)) || 0
  }, [coupon, amountOff, selectedPlanPrice])

  const currentRequestPacksTotal = currentRequestPacksQuantity * REQUEST_PACK_PRICE
  const requestPacksTotal = requestPacksQuantity * REQUEST_PACK_PRICE
  const totalGross = Number(selectedPlanPrice + requestPacksTotal)
  const discount = Number(amountOff > 0 ? amountOff : totalGross * (percentOff / 100)) || 0
  const totalNet = totalGross - discount
  const totalAmount = totalNet + estimatedTax

  const priceForPlan = isDowngradingToFree ? 0 : selectedPlanPrice
  const priceForAddon = requestPacksTotal

  const subtotal = priceForPlan + priceForAddon

  const amountToPay = subtotal + estimatedTax - discount
  const dueAmountToday = Math.max(amountToPay - currentRequestPacksTotal - currentPlanPrice, 0)

  const renderDiscount = () => (
    <div className='flex items-center justify-between'>
      <div className='text-text-alternative flex max-sm:basis-1/2' data-testid='subscription-summary-coupon-discount'>
        {amountOff > 0 ? (
          <div>
            Discount - ({coupon?.code}) US{money.format(amountOff)} off
          </div>
        ) : (
          <div>
            Discount - ({coupon?.code}) {Number(percentOff).toFixed(2)}% off
          </div>
        )}
        <ActionLink
          data-testid='subscription-summary-clear-coupon-code'
          icon={faTimes}
          onClick={() => {
            setCouponCode('')
            setCoupon(undefined)
            if (sessionStorage.hasOwnProperty(sessionStoragePromoCode)) {
              sessionStorage.removeItem(sessionStoragePromoCode)
            }
          }}
        />
      </div>
      {couponLoading ? (
        <Loader className='h-[21px]' pulse />
      ) : (
        <div className={'text-success-default'} data-testid='subscription-summary-discount'>
          <span>-US</span>
          <span>{money.format(amountOff > 0 ? amountOff : discount)}</span>
        </div>
      )}
    </div>
  )
  const renderCouponCodeForm = () => (
    <Form
      initialValues={formState}
      keepDirtyOnReinitialize
      onSubmit={() => {}}
      render={() => (
        <form className='w-full'>
          <div className='flex'>
            <Field name='coupon_code'>
              {() => (
                <Input
                  button={
                    <Button
                      data-testid='subscription-summary-apply-coupon-code'
                      disabled={validatingCoupon}
                      loading={validatingCoupon}
                      onClick={() => {
                        handleValidatePromoCode()
                      }}
                      theme='secondary'
                      type='button'
                    >
                      Apply
                    </Button>
                  }
                  className='focus:border-primary-default focus:shadow-primary-default focus:ring-primary-default'
                  data-testid='subscription-summary-coupon-code'
                  error={couponCodeError}
                  name='coupon_code'
                  onChange={e => {
                    setCouponCode(e.currentTarget.value)
                  }}
                  placeholder='Add discount code'
                  showErrorMessage={false}
                  type='text'
                />
              )}
            </Field>
            <ActionLink
              className='mb'
              data-testid='subscription-summary-hide-form'
              icon={faTimes}
              onClick={() => {
                setIsCouponCodeFormVisible(false)
                if (sessionStorage.hasOwnProperty(sessionStoragePromoCode)) {
                  sessionStorage.removeItem(sessionStoragePromoCode)
                }
              }}
            />
          </div>
          {couponCodeError ? (
            <div className='mt-2 flex items-center text-red-500'>
              <FontAwesomeIcon className='mr-2' icon={faExclamationTriangle} />
              <Text as='span' data-testid='coupon-error' theme='sm'>
                {couponCodeError}
              </Text>
            </div>
          ) : null}
        </form>
      )}
    />
  )

  const getSummary = () => {
    const tierOffering = tiers[selectedPlan]
    const dailyUnits = tierOffering?.creditsPerDay ?? 0
    const dailyAddonUnits = addons.traffic_addon?.reqThreshold || 0
    const totalDailyAddonUnits = (dailyAddonUnits || 0) * requestPacksQuantity
    const totalDailyUnits = isOnCreditPlan
      ? displayAsMillions(dailyUnits + totalDailyAddonUnits)
      : numberWithCommas(dailyUnits + totalDailyAddonUnits)
    const dailyLabel = `credits per day`
    const addonLabel = 'credit packs'
    const summaryAction = isDowngrading ? 'downgraded' : 'upgraded'

    // downgrade to core plan
    if (isDowngrading && selectedPlan === ServiceTier.CORE) {
      return (
        <>
          You will be downgraded to our free plan and have access to {totalDailyUnits} {dailyLabel}. Your {addonLabel}{' '}
          are removed because they are exclusive to our paid plans. Your estimated monthly total is US$0.00.
        </>
      )
    }

    // upgrade or downgrade credit/request packs Only
    if (isPlanUnchanged) {
      return (
        <>
          You will be {summaryAction} to {requestPacksQuantity} {addonLabel} and have access to {totalDailyUnits}{' '}
          {dailyLabel}. Your estimated monthly total is US{money.format(totalAmount)}. Please note that your next bill
          is due {dueDate}, unless you decide to cancel.
        </>
      )
    }

    // downgrade plan + credit/request packs changes
    if (isPlanDowngrade && !isAddonUnchanged) {
      const currentTierOffering = tiers[currentPlan]
      const currentDailyUnits =
        (isOnCreditPlan ? currentTierOffering?.creditsPerDay : currentTierOffering?.reqThreshold) ?? 0
      const currentTotalDailyUnits = isOnCreditPlan
        ? displayAsMillions(currentDailyUnits + totalDailyAddonUnits)
        : numberWithCommas(currentDailyUnits + totalDailyAddonUnits)

      return (
        <>
          You will maintain your current plan and have access to {currentTotalDailyUnits} {dailyLabel} until the end of
          your current billing period on {dueDate}, after that your subscription will be {summaryAction} to our{' '}
          {tierOffering?.name} plan and have access to {totalDailyUnits} {dailyLabel}. Your estimated monthly total is
          US
          {money.format(totalAmount)}. Please note that your next bill is due {dueDate}, unless you decide to cancel.
        </>
      )
    }

    // downgrade plan only
    if (isPlanDowngrade) {
      return (
        <>
          You will maintain your current plan until the end of your current billing period on {dueDate}, after that your
          subscription will be {summaryAction} to our {tierOffering?.name} plan and have access to {totalDailyUnits}{' '}
          {dailyLabel}. Your estimated monthly total is US{money.format(totalAmount)}. Please note that your next bill
          is due {dueDate}, unless you decide to cancel.
        </>
      )
    }

    // plan upgrades and any other combinations
    return (
      <>
        You will be {summaryAction} to {tierOffering?.name} plan and have access to {totalDailyUnits} {dailyLabel}. Your
        estimated monthly total is US{money.format(totalAmount)}. Please note that your next bill is due {dueDate},
        unless you decide to cancel.
      </>
    )
  }

  return (
    <>
      {tiers[selectedPlan] ? (
        <Card className='mb-4 py-4 text-left'>
          <div className='border-border-muted border-b text-sm'>
            <div className='mb-4 flex flex-wrap items-center justify-between sm:flex-nowrap'>
              <div />
              <Heading data-testid={SUBSCRIPTION_SUMMARY_TITLE_DATA_TEST_ID} theme='section'>
                Subscription Summary
              </Heading>
              {checkoutCloseCallback ? (
                <ActionLink
                  data-testid='checkout-modal-close'
                  icon={faTimes}
                  onClick={() => {
                    checkoutCloseCallback()
                    if (sessionStorage.hasOwnProperty(sessionStoragePromoCode)) {
                      sessionStorage.removeItem(sessionStoragePromoCode)
                    }
                  }}
                />
              ) : (
                <div />
              )}
            </div>
            <UpgradeDowngradeDetails
              currentPlan={currentPlan}
              currentRequestPacksQuantity={currentRequestPacksQuantity}
              currentRequestPacksTotal={currentRequestPacksTotal}
              currentThroughputAddonEnabled={currentThroughputAddonEnabled}
              isDowngrading={isDowngrading}
              isDowngradingToFree={isDowngradingToFree}
              requestPacksQuantity={requestPacksQuantity}
              requestPacksTotal={requestPacksTotal}
              selectedPlan={selectedPlan}
              selectedPlanPrice={selectedPlanPrice}
              throughputAddonEnabled={throughputAddonEnabled}
              tiers={tiers}
            />
            {!isDowngrading ? (
              <div className='mb-4'>
                {couponLoading ? (
                  <Loader className='h-[21px] w-full' pulse />
                ) : coupon ? (
                  isCouponCodeFormVisible ? (
                    renderCouponCodeForm()
                  ) : (
                    renderDiscount()
                  )
                ) : isCouponCodeFormVisible ? (
                  renderCouponCodeForm()
                ) : (
                  <ActionLink
                    data-testid='add-discount-code'
                    onClick={() => {
                      setIsCouponCodeFormVisible(true)
                    }}
                  >
                    Add Discount Code
                  </ActionLink>
                )}
              </div>
            ) : null}
          </div>

          <>
            <div className='mt-4 flex items-center justify-between text-sm'>
              <div className='text-text-default font-medium'>Subtotal</div>
              <div className='text-text-default font-medium' data-testid='checkout-modal-subtotal'>
                US{money.format(subtotal <= 0 ? 0 : subtotal)}
              </div>
            </div>
            {hasPaymentMethod && !isPaymentFormVisible && !isDowngrading ? (
              <div className='text-text-default mt-2 flex items-center justify-between text-sm'>
                <div>Estimated tax</div>
                <div data-testid='checkout-modal-estimated-tax'>
                  <SubscriptionSummaryTaxes
                    coupon={coupon}
                    estimatedTax={estimatedTax}
                    isPaymentFormVisible={isPaymentFormVisible}
                    requestPacks={requestPacksQuantity}
                    selectedPlan={selectedPlan}
                    setEstimatedTax={setEstimatedTax}
                    setIsTaxLoading={setIsTaxLoading}
                  />
                </div>
              </div>
            ) : null}
          </>
          <span>
            {hasPaymentMethod && !isPaymentFormVisible ? (
              <div className={clsx('border-border-muted mt-4 grid grid-cols-1 gap-y-4 border-t pt-4')}>
                <div className='flex justify-between'>
                  <div>
                    <Heading as='h4'>Due Today</Heading>
                  </div>
                  <div className='text-text-default font-bold' data-testid='checkout-modal-due-today'>
                    {!isTaxLoading || isDowngrading ? (
                      `US${money.format(dueAmountToday)}`
                    ) : (
                      <Loader className='w-18 h-[21px]' pulse />
                    )}
                  </div>
                </div>
                {
                  <div>
                    <div className='mb-4'>
                      <Button
                        data-testid='checkout-modal-subscribe-button'
                        disabled={isPaymentSubmitting}
                        fullWidth
                        onClick={() =>
                          handleSubscription({
                            addons: [
                              {
                                quantity: requestPacksQuantity || 0,
                                selected: requestPacksQuantity > 0,
                                type: ServiceAddon.TRAFFIC_ADDON
                              },
                              {
                                quantity: 1,
                                selected: throughputAddonEnabled,
                                type: ServiceAddon.THROUGHPUT_ADDON
                              }
                            ],
                            tier: selectedPlan
                          })
                        }
                      >
                        {isPaymentSubmitting ? (
                          <FontAwesomeIcon className='fa-fw fa-spin' icon={faSpinnerThird} />
                        ) : (
                          'Change Plan'
                        )}
                      </Button>
                    </div>
                    <div className='text-text-default text-sm' data-testid='checkout-modal-summary'>
                      {getSummary()}
                    </div>
                  </div>
                }
              </div>
            ) : null}
          </span>
        </Card>
      ) : null}
    </>
  )
}

const UpgradeDowngradeDetails = ({
  currentPlan,
  currentRequestPacksQuantity,
  currentRequestPacksTotal,
  currentThroughputAddonEnabled,
  isDowngrading,
  isDowngradingToFree,
  requestPacksQuantity,
  requestPacksTotal,
  selectedPlan,
  selectedPlanPrice,
  throughputAddonEnabled,
  tiers
}: {
  tiers: TierOfferings
  currentPlan: ServiceTier
  currentRequestPacksQuantity: number
  currentRequestPacksTotal: number
  currentThroughputAddonEnabled: boolean
  throughputAddonEnabled: boolean
  isDowngrading: boolean
  selectedPlan: ServiceTier
  isDowngradingToFree: boolean
  selectedPlanPrice: number
  requestPacksQuantity: number
  requestPacksTotal: number
}) => {
  return (
    <div className='mb-2 items-center overflow-auto sm:flex'>
      <div className='mb-4 basis-1/2 sm:mb-0'>
        <Heading as='h4'>Current</Heading>
        <div className='flex items-center justify-between'>
          <p data-testid='checkout-modal-current-plan'>{tiers?.[currentPlan]?.name}</p>
          <p data-testid='checkout-modal-current-plan-price'>
            {currentPlan === ServiceTier.CORE ? 'Free' : `US${money.format(tiers?.[currentPlan]?.price ?? 0)}`}
          </p>
        </div>
        {currentRequestPacksQuantity > 0 ? (
          <div className='flex items-center justify-between'>
            <span className='text-text-default' data-testid='current-request-packs'>
              Credit Packs
            </span>
            <span className='text-text-default' data-testid='current-request-packs-price'>
              US{money.format(currentRequestPacksTotal)} (QTY {currentRequestPacksQuantity})
            </span>
          </div>
        ) : null}
        {throughputAddonEnabled && !currentThroughputAddonEnabled ? (
          <div className='flex items-center justify-between'>
            <span className='text-text-default' data-testid='current-request-packs'>
              Burstable Throughput
            </span>
            <span className='text-text-default' data-testid='current-request-packs-price'>
              Disabled
            </span>
          </div>
        ) : null}
        {!throughputAddonEnabled && currentThroughputAddonEnabled ? (
          <div className='flex items-center justify-between'>
            <span className='text-text-default' data-testid='current-request-packs'>
              Burstable Throughput
            </span>
            <span className='text-text-default' data-testid='current-request-packs-price'>
              Enabled
            </span>
          </div>
        ) : null}
      </div>
      <div className='hidden h-[70px] items-center px-3 sm:flex'>
        <FontAwesomeIcon className='mr-2 text-gray-400' icon={faAngleDoubleRight} size='xl' />
      </div>
      <div className='basis-1/2'>
        <Heading as='h4'>{isDowngrading ? 'Downgrade' : 'Upgrade'}</Heading>
        <div className='flex items-center justify-between'>
          <p data-testid='checkout-modal-selected-plan'>{tiers[selectedPlan]?.name}</p>
          <p data-testid='checkout-modal-selected-plan-price'>
            {isDowngradingToFree ? 'Free' : `US${money.format(selectedPlanPrice)}`}
          </p>
        </div>
        {requestPacksQuantity > 0 && !isDowngradingToFree ? (
          <div className='flex items-center justify-between'>
            <p data-testid='selected-request-packs'>Credit Packs</p>
            <p data-testid='selected-request-packs-price'>
              US{money.format(requestPacksTotal)} (QTY {requestPacksQuantity})
            </p>
          </div>
        ) : null}

        {throughputAddonEnabled && !currentThroughputAddonEnabled ? (
          <div className='flex items-center justify-between'>
            <span className='text-text-default' data-testid='current-request-packs'>
              Burstable Throughput
            </span>
            <span className='text-text-default' data-testid='current-request-packs-price'>
              Enabled
            </span>
          </div>
        ) : null}

        {!throughputAddonEnabled && currentThroughputAddonEnabled ? (
          <div className='flex items-center justify-between'>
            <span className='text-text-default' data-testid='current-request-packs'>
              Burstable Throughput
            </span>
            <span className='text-text-default' data-testid='current-request-packs-price'>
              Disabled
            </span>
          </div>
        ) : null}
      </div>
    </div>
  )
}
